import { homepageData } from "@/mocks/homeData";
import { useEntranceAnimation } from "@/hooks/useEntranceAnimation";

export default function CoreDirectionsSection() {
  const { coreDirections } = homepageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.08, sectionIndex: 1 });

  const topThree = coreDirections.directions.slice(0, 3);
  const bottomTwo = coreDirections.directions.slice(3);

  const renderCard = (dir: typeof coreDirections.directions[0], idx: number, groupOffset: number) => {
    const globalIdx = groupOffset + idx;
    return (
      <div
        key={dir.id}
        className={`group relative bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 transition-all duration-[600ms] ease-out hover:border-primary-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-background-200/50 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
        style={{ transitionDelay: `${180 + globalIdx * 100}ms` }}
      >
        {/* Number badge */}
        <span className="absolute top-4 right-4 font-heading text-2xl font-bold text-background-200 group-hover:text-primary-200 transition-colors duration-300">
          {String(globalIdx + 1).padStart(2, "0")}
        </span>

        {/* Icon */}
        <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-4 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors duration-300">
          <i className={`${dir.icon} text-xl`} />
        </div>

        {/* Title */}
        <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 pr-8">
          {dir.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-foreground-600 leading-relaxed">
          {dir.description}
        </p>
      </div>
    );
  };

  return (
    <section
      ref={ref}
      className="relative bg-background-100"
      style={{ minHeight: "100svh" }}
    >
      <div className="page-container py-16 md:py-20 w-full flex flex-col justify-center" style={{ minHeight: "100svh" }}>
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <div
            className={`transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            <span className="text-[11px] tracking-[0.25em] uppercase text-foreground-400">
              Publishing
            </span>
          </div>
          <div
            className={`mt-2 transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "60ms" }}
          >
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 tracking-wide leading-tight">
              {coreDirections.title}
            </h2>
          </div>
          {coreDirections.description && (
            <div
              className={`mt-4 md:mt-5 transition-all duration-[600ms] ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "120ms" }}
            >
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {coreDirections.description}
              </p>
            </div>
          )}
        </div>

        {/* Top row: 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
          {topThree.map((dir, idx) => renderCard(dir, idx, 0))}
        </div>

        {/* Bottom row: 2 cards centered */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-[680px] mx-auto mt-5 md:mt-6">
          {bottomTwo.map((dir, idx) => renderCard(dir, idx, 3))}
        </div>
      </div>
    </section>
  );
}